<template>
  <NodePage v-if="pageNode" v-bind="pageNode">
    <div class="container">
      <div>
        <SearchInput v-model="text" />

        <div class="tags flex flex-row flex-wrap mb-60 mt-30">
          <FilterTag
            :label="$texts('allCategories', 'All categories')"
            query-key="category"
          />
          <FilterTag
            v-for="group in groups"
            :key="group.id"
            query-key="category"
            :query-value="group.id"
            :label="group.title"
          />
        </div>
      </div>
      <div
        v-for="group in groupsMapped"
        v-show="group.visible"
        :key="group.title"
        class="mb-100"
      >
        <h2 class="uppercase font-bold mb-10">{{ group.title }}</h2>
        <div class="border-t-[0.75px]">
          <Accordion
            v-for="item in group.items"
            v-show="item.visible"
            :key="group.id + item.id"
            :title="item.node.label || ''"
          >
            <div class="content pb-20" v-html="item.node.body" />
          </Accordion>
        </div>
      </div>
    </div>
  </NodePage>
</template>

<script lang="ts" setup>
import type { FaqOverviewNodeFragment } from '#build/graphql-operations'
import { filterByTypenames } from '~/helpers/type'

definePageMeta({
  name: 'faqs',
  path: '/faqs',
  drupalFrontendRoute: true,
  drupalRoute: true,
  languageMapping: {
    en: '/faqs',
    fr: '/faqs',
  },
})

const route = useRoute()
const { region } = useCountry()
const text = useQueryString('text')
const activeCategory = useQueryString('category')

const { data: query } = await useAsyncGraphqlQuery(
  'faqNodes',
  {
    path: route.path,
  },
  {
    graphqlCaching: {
      client: true,
    },

    transform: function (data) {
      return data.data
    },
  },
)

const { entity: pageNode, metatags } = await useDrupalRouteQuery('slug')
defineSchemaOrg(metatags)

const nodes = computed(() =>
  filterByTypenames(query.value?.entityQuery.items, 'NodeFaq'),
)

const searchQuery = computed(() => ({
  region: region.value,
  text: text.value,
}))

const { data: visibleNodeIds } = await useFetch('/api/search/faqs', {
  query: searchQuery,
})

type FaqGroupItem = {
  visible?: boolean
  id: string
  node: FaqOverviewNodeFragment
}

type FaqGroup = {
  visible?: boolean
  title: string
  id: string
  weight: number
  items: FaqGroupItem[]
}

const groups = computed(() =>
  Object.values(
    (nodes.value || []).reduce<Record<string, FaqGroup>>((acc, node) => {
      const faqCategories = node.categories || []
      faqCategories.forEach((category) => {
        if (category.id && category.label && node.id) {
          if (!acc[category.id]) {
            acc[category.id] = {
              title: category.label,
              id: category.id,
              items: [],
              weight: category.weight || 9999,
            }
          }

          acc[category.id]!.items.push({
            id: node.id,
            node,
          })
        }
      })

      return acc
    }, {}),
  ).sort((a, b) => {
    // move general information to top of list
    const generalInformation = '753'
    return a.id === generalInformation
      ? -1
      : b.id === generalInformation
        ? 1
        : a.weight - b.weight
  }),
)

const groupsMapped = computed(() => {
  return groups.value.map((group) => {
    const items = group.items.map((item) => {
      return {
        ...item,
        visible: visibleNodeIds.value?.includes(item.id),
      }
    })
    const hasSomeVisible = items.some((item) => item.visible)

    return {
      ...group,
      items,
      visible:
        hasSomeVisible &&
        (!activeCategory.value || group.id === activeCategory.value),
    }
  })
})

await renderPageDependencies()
</script>
